import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { GESPRAECH_ERGEBNIS_BADGE_CLASS, userAnzeige } from '../lib/aktivitaet.js';
import { formatGeld } from '../lib/format.js';
import { praxisArtOrDefault, wirtschaftsmodellOrDefault } from '../lib/praxis.js';
import { FACHRICHTUNG_LABEL } from '../lib/fachrichtung.js';
import { ZEITRAUM_OPTIONS, bereichFuer, toPbStart, toPbEnde } from '../lib/zeitraum.js';

const EMPTY_STATS = {
  telefonate: 0,
  telefonat_entscheider: 0,
  telefonat_nicht_entscheider: 0,
  telefonat_nicht_erreicht: 0,
  aufgaben_offen: 0,
  aufgaben_erledigt: 0,
  emails: 0,
  besuche: 0,
  neue_praxen: 0,
  neue_kontakte: 0,
  gesamt: 0,
};

function StatNumber({ label, value }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default function AuswertungPage() {
  // Zeitraum
  const [zeitraum, setZeitraum] = useState('monat');
  const [individuell, setIndividuell] = useState(() => {
    const m = bereichFuer('monat');
    return { start: m.start, ende: m.ende };
  });

  const aktiverBereich = useMemo(() => {
    if (zeitraum === 'individuell') return individuell;
    return bereichFuer(zeitraum);
  }, [zeitraum, individuell]);

  // Daten
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(new Map());
  const [umsatzUebersicht, setUmsatzUebersicht] = useState({
    gesamt: 0,
    praxenMitUmsatz: 0,
    praxenOhneUmsatz: 0,
    top: [],
  });
  const [wirtschaftsmodellUnbekannt, setWirtschaftsmodellUnbekannt] = useState(0);
  const [praxisArtCounts, setPraxisArtCounts] = useState({
    privatpraxis: 0,
    gemischt: 0,
    unbekannt: 0,
  });
  // Map<slug | '__leer__', count> — anschließend in Listenform sortiert.
  const [fachrichtungCounts, setFachrichtungCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!aktiverBereich?.start || !aktiverBereich?.ende) return;
    setLoading(true);
    setError(null);
    try {
      const startPb = toPbStart(aktiverBereich.start);
      const endePb = toPbEnde(aktiverBereich.ende);
      const filter = `created >= "${startPb}" && created <= "${endePb}"`;

      const [usersList, aktivitaeten, neuePraxen, neueKontakte, allePraxen] =
        await Promise.all([
          pb.collection('users').getFullList({ sort: 'nachname,vorname,email' }),
          pb.collection('aktivitaeten').getFullList({
            filter,
            fields: 'id,typ,status,erstellt_von,gespraech_ergebnis',
          }),
          pb.collection('praxen').getFullList({
            filter,
            fields: 'id,angelegt_von',
          }),
          pb.collection('kontakte').getFullList({
            filter,
            fields: 'id,angelegt_von',
          }),
          pb.collection('praxen').getFullList({
            fields:
              'id,name,name_zusatz,umsatz_goae,umsatz_uvgoae,plz,ort,wirtschaftsmodell,' +
              'praxis_art,fachgebiet_neu',
          }),
        ]);

      // Pro User aggregieren — nur Vertriebler (alle Rollen) zählen.
      const map = new Map();
      for (const u of usersList) {
        map.set(u.id, { user: u, ...EMPTY_STATS });
      }

      for (const a of aktivitaeten) {
        const s = map.get(a.erstellt_von);
        if (!s) continue;
        if (a.typ === 'telefonat') {
          s.telefonate += 1;
          if (a.gespraech_ergebnis === 'entscheider') s.telefonat_entscheider += 1;
          else if (a.gespraech_ergebnis === 'nicht_entscheider') s.telefonat_nicht_entscheider += 1;
          else if (a.gespraech_ergebnis === 'nicht_erreicht') s.telefonat_nicht_erreicht += 1;
        } else if (a.typ === 'email') s.emails += 1;
        else if (a.typ === 'besuch') s.besuche += 1;
        else if (a.typ === 'aufgabe') {
          if (a.status === 'erledigt') s.aufgaben_erledigt += 1;
          else s.aufgaben_offen += 1;
        }
        s.gesamt += 1;
      }

      for (const p of neuePraxen) {
        const s = map.get(p.angelegt_von);
        if (s) s.neue_praxen += 1;
      }

      for (const k of neueKontakte) {
        const s = map.get(k.angelegt_von);
        if (s) s.neue_kontakte += 1;
      }

      setUsers(usersList);
      setStats(map);

      // Umsatz-Übersicht — über ALLE Praxen, nicht zeitraumgebunden.
      let summe = 0;
      let mitUmsatz = 0;
      let ohneUmsatz = 0;
      let wmUnbekannt = 0;
      const artCounts = { privatpraxis: 0, gemischt: 0, unbekannt: 0 };
      const fachCounter = new Map();
      const mitWerten = [];
      for (const p of allePraxen) {
        const goae = Number(p.umsatz_goae || 0);
        const uvgoae = Number(p.umsatz_uvgoae || 0);
        const total = goae + uvgoae;
        if (total > 0) {
          summe += total;
          mitUmsatz += 1;
          mitWerten.push({ ...p, summe: total });
        } else {
          ohneUmsatz += 1;
        }
        if (wirtschaftsmodellOrDefault(p.wirtschaftsmodell) === 'unbekannt') {
          wmUnbekannt += 1;
        }
        const art = praxisArtOrDefault(p.praxis_art);
        if (art in artCounts) artCounts[art] += 1;

        const fach = p.fachgebiet_neu || '__leer__';
        fachCounter.set(fach, (fachCounter.get(fach) ?? 0) + 1);
      }
      const top = mitWerten.sort((a, b) => b.summe - a.summe).slice(0, 10);
      setUmsatzUebersicht({
        gesamt: summe,
        praxenMitUmsatz: mitUmsatz,
        praxenOhneUmsatz: ohneUmsatz,
        top,
      });
      setWirtschaftsmodellUnbekannt(wmUnbekannt);
      setPraxisArtCounts(artCounts);

      // Fachrichtungs-Liste: nach Anzahl absteigend, bei Gleichstand alphabetisch.
      // Praxen ohne strukturierte Fachrichtung kommen unter 'leer' separat ans Ende.
      const fachListe = [...fachCounter.entries()]
        .map(([slug, count]) => ({
          slug,
          label: slug === '__leer__' ? '— nicht erfasst —' : FACHRICHTUNG_LABEL[slug] ?? slug,
          count,
        }))
        .sort((a, b) => {
          if (a.slug === '__leer__') return 1;
          if (b.slug === '__leer__') return -1;
          if (b.count !== a.count) return b.count - a.count;
          return a.label.localeCompare(b.label, 'de');
        });
      setFachrichtungCounts(fachListe);
    } catch (err) {
      setError(err?.message || 'Auswertung konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [aktiverBereich?.start, aktiverBereich?.ende]);

  useEffect(() => {
    load();
  }, [load]);

  // Sortiert nach Aktivitäten gesamt — User ohne jegliche Aktivität raus.
  const sortedStats = useMemo(() => {
    return [...stats.values()]
      .filter(
        (s) =>
          s.gesamt > 0 ||
          s.neue_praxen > 0 ||
          s.neue_kontakte > 0
      )
      .sort((a, b) => b.gesamt - a.gesamt);
  }, [stats]);

  // Telefonat-Auswertung: nur User mit Telefonaten, sortiert nach Anzahl.
  const telefonatStats = useMemo(() => {
    return [...stats.values()]
      .filter((s) => s.telefonate > 0)
      .map((s) => ({
        ...s,
        erfolgsquote: s.telefonate > 0 ? (s.telefonat_entscheider / s.telefonate) * 100 : 0,
      }))
      .sort((a, b) => b.telefonate - a.telefonate);
  }, [stats]);

  const maxTelefonate = useMemo(
    () => telefonatStats.reduce((m, s) => Math.max(m, s.telefonate), 0),
    [telefonatStats]
  );

  function handleZeitraumChange(value) {
    setZeitraum(value);
    if (value !== 'individuell') {
      const b = bereichFuer(value);
      if (b) setIndividuell(b);
    }
  }

  function handleIndividuellChange(field, value) {
    setIndividuell((prev) => ({ ...prev, [field]: value }));
    setZeitraum('individuell');
  }

  function exportCsv() {
    const headers = [
      'Name',
      'E-Mail',
      'Telefonate',
      'Aufgaben offen',
      'Aufgaben erledigt',
      'E-Mails',
      'Besuche',
      'Neue Praxen',
      'Neue Kontakte',
      'Aktivitäten gesamt',
    ];
    const lines = [headers.join(';')];
    for (const s of sortedStats) {
      lines.push(
        [
          `"${userAnzeige(s.user)}"`,
          `"${s.user.email ?? ''}"`,
          s.telefonate,
          s.aufgaben_offen,
          s.aufgaben_erledigt,
          s.emails,
          s.besuche,
          s.neue_praxen,
          s.neue_kontakte,
          s.gesamt,
        ].join(';')
      );
    }
    // BOM voranstellen, damit Excel UTF-8 korrekt erkennt.
    const csv = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auswertung_${aktiverBereich.start}_${aktiverBereich.ende}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Auswertung</h1>
        <p className="mt-1 text-sm text-slate-600">
          Vertriebler-Aktivitäten und Umsatzpotenzial im Überblick.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      {/* Zeitraum-Picker */}
      <div className="card space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Zeitraum:
          </span>
          {ZEITRAUM_OPTIONS.map((opt) => {
            const isActive = zeitraum === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleZeitraumChange(opt.value)}
                className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {zeitraum === 'individuell' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Von</label>
              <input
                type="date"
                className="input mt-1"
                value={individuell.start}
                onChange={(e) => handleIndividuellChange('start', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Bis</label>
              <input
                type="date"
                className="input mt-1"
                value={individuell.ende}
                onChange={(e) => handleIndividuellChange('ende', e.target.value)}
              />
            </div>
          </div>
        )}

        <p className="text-xs text-slate-500">
          Aktiver Zeitraum: {aktiverBereich?.start} bis {aktiverBereich?.ende}
        </p>
      </div>

      {/* Wirtschaftsmodell-Datenqualität */}
      <div className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Wirtschaftsmodell</h2>
          <p className="text-sm text-slate-500">
            Wie viele Praxen wurden schon eingeordnet?
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-1">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Noch unbekannt
            </div>
            <div className="mt-1 text-2xl font-bold text-slate-700">
              {wirtschaftsmodellUnbekannt}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Praxen, deren Wirtschaftsmodell noch nicht erfragt wurde.
            </p>
          </div>
        </div>
      </div>

      {/* Praxen nach Art */}
      <div className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Praxen nach Art</h2>
          <p className="text-sm text-slate-500">
            Verteilung aller Praxen nach Art (Privatpraxis vs. gemischt).
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-md border border-purple-200 bg-purple-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-purple-700">
              Privatpraxis
            </div>
            <div className="mt-1 text-2xl font-bold text-purple-900">
              {praxisArtCounts.privatpraxis}
            </div>
          </div>
          <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-blue-700">
              Gemischt
            </div>
            <div className="mt-1 text-2xl font-bold text-blue-900">
              {praxisArtCounts.gemischt}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Unbekannt
            </div>
            <div className="mt-1 text-2xl font-bold text-slate-700">
              {praxisArtCounts.unbekannt}
            </div>
          </div>
        </div>
      </div>

      {/* Praxen nach Fachrichtung */}
      <div className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Praxen nach Fachrichtung</h2>
          <p className="text-sm text-slate-500">
            Verteilung nach strukturierter Fachrichtung — sortiert nach Anzahl.
          </p>
        </div>

        {fachrichtungCounts.length === 0 && (
          <p className="text-sm text-slate-500">Noch keine Daten erfasst.</p>
        )}

        {fachrichtungCounts.length > 0 && (() => {
          const max = Math.max(...fachrichtungCounts.map((f) => f.count), 1);
          return (
            <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Fachrichtung
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Anzahl Praxen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {fachrichtungCounts.map((f) => {
                    const breite = (f.count / max) * 100;
                    const istLeer = f.slug === '__leer__';
                    return (
                      <tr key={f.slug} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-sm">
                          <span
                            className={
                              istLeer ? 'italic text-slate-500' : 'text-slate-800'
                            }
                          >
                            {f.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className={`h-full ${
                                  istLeer ? 'bg-slate-300' : 'bg-primary-500'
                                }`}
                                style={{ width: `${breite}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-slate-800">
                              {f.count}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Umsatz-Übersicht */}
      <div className="card space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Umsatzpotenzial</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-md border border-primary-200 bg-primary-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-primary-700">
              Gesamt aller Praxen
            </div>
            <div className="mt-1 text-2xl font-bold text-primary-900">
              {formatGeld(umsatzUebersicht.gesamt) || '—'}
            </div>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-emerald-700">
              Mit Umsatzdaten
            </div>
            <div className="mt-1 text-2xl font-bold text-emerald-900">
              {umsatzUebersicht.praxenMitUmsatz}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Ohne Umsatzdaten
            </div>
            <div className="mt-1 text-2xl font-bold text-slate-700">
              {umsatzUebersicht.praxenOhneUmsatz}
            </div>
          </div>
        </div>

        {umsatzUebersicht.top.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Top 10 Praxen nach Umsatzpotenzial
            </h3>
            <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      #
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Praxis
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Ort
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Umsatz
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {umsatzUebersicht.top.map((p, i) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-sm text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 text-sm">
                        <Link
                          to={`/praxen/${p.id}`}
                          className="font-medium text-primary-700 hover:underline"
                        >
                          {praxisAnzeige(p)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-600">
                        {[p.plz, p.ort].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-semibold text-slate-800">
                        {formatGeld(p.summe)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Telefonat-Auswertung */}
      <div className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Telefonat-Auswertung</h2>
          <p className="text-sm text-slate-500">
            Telefonate pro Vertriebler im gewählten Zeitraum mit Erfolgsquote
            (Anteil Gespräche mit Entscheider).
          </p>
        </div>

        {!loading && telefonatStats.length === 0 && (
          <p className="text-sm text-slate-500">
            Keine Telefonate im gewählten Zeitraum erfasst.
          </p>
        )}

        {telefonatStats.length > 0 && (
          <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Vertriebler
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Telefonate
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-emerald-700">
                    Entscheider
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-amber-700">
                    Nicht-Entscheider
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Nicht erreicht
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Erfolgsquote
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {telefonatStats.map((s) => {
                  const balkenBreite = maxTelefonate > 0 ? (s.telefonate / maxTelefonate) * 100 : 0;
                  return (
                    <tr key={s.user.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-slate-900">
                          {userAnzeige(s.user)}
                        </div>
                        <div className="text-xs text-slate-500">{s.user.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full bg-primary-500"
                              style={{ width: `${balkenBreite}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-slate-800">
                            {s.telefonate}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${GESPRAECH_ERGEBNIS_BADGE_CLASS.entscheider}`}
                        >
                          {s.telefonat_entscheider}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${GESPRAECH_ERGEBNIS_BADGE_CLASS.nicht_entscheider}`}
                        >
                          {s.telefonat_nicht_entscheider}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${GESPRAECH_ERGEBNIS_BADGE_CLASS.nicht_erreicht}`}
                        >
                          {s.telefonat_nicht_erreicht}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-semibold text-slate-800">
                        {s.erfolgsquote.toFixed(1)} %
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vertriebler-Statistiken */}
      <div className="card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Vertriebler</h2>
            <p className="text-sm text-slate-500">
              Sortiert nach Aktivitäten gesamt im gewählten Zeitraum.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={exportCsv}
            disabled={sortedStats.length === 0}
          >
            CSV exportieren
          </button>
        </div>

        {loading && <p className="text-sm text-slate-500">Lade …</p>}
        {!loading && sortedStats.length === 0 && (
          <p className="text-sm text-slate-500">
            Keine Aktivitäten im gewählten Zeitraum gefunden.
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {sortedStats.map((s) => (
            <div key={s.user.id} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    {userAnzeige(s.user)}
                  </div>
                  <div className="text-xs text-slate-500">{s.user.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium uppercase text-slate-500">Gesamt</div>
                  <div className="text-2xl font-bold text-primary-700">{s.gesamt}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <StatNumber label="Telefonate" value={s.telefonate} />
                <StatNumber label="E-Mails" value={s.emails} />
                <StatNumber label="Besuche" value={s.besuche} />
                <StatNumber label="Aufg. offen" value={s.aufgaben_offen} />
                <StatNumber label="Aufg. erledigt" value={s.aufgaben_erledigt} />
                <StatNumber label="Neue Praxen" value={s.neue_praxen} />
                <StatNumber label="Neue Kontakte" value={s.neue_kontakte} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
